/**
 * ReportSnapshotHandlerService
 *
 * Iterates through an IReportSnapshotTemplate, creates a temporary standalone
 * viewport for each config (hidden off-screen), optionally selects and navigates
 * to a measurement (activating overlays via the display feature system), then
 * delegates the actual capture+storage to ViewportSnapshotService.captureViewportById().
 *
 * Returning the IDs of the newly stored snapshots lets the caller distinguish
 * "template" snapshots from manually taken user snapshots in the same store.
 */

import { resolveSnapshotFieldOfViewMm } from './snapshot-field-of-view';
import type { IReportSnapshotTemplate, ISnapshotViewportConfig } from './types';

import type { SizingStateService } from '@/visualization/services/sizing/sizing-state-service';
import type { ViewportService } from '@/visualization/services/viewport-service';
import type { ViewportSnapshotService } from '@/visualization/services/viewport-snapshot-service';
import { VIEW_MODE_TO_VIEWPORT_TYPE, ViewportType } from '@/visualization/viewports/types';

/** Resolve on the next animation frame, giving the browser a chance to flush GPU work. */
function nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

// ============================================================================
// Service
// ============================================================================

export class ReportSnapshotHandlerService {
    constructor(
        private readonly viewportService: ViewportService,
        private readonly snapshotService: ViewportSnapshotService,
        /**
         * Optional. When provided, measurementId configs will select and navigate
         * to the measurement before capturing, activating the sizing overlay.
         */
        private readonly sizingStateService?: SizingStateService
    ) {}

    /**
     * Capture all snapshots defined in the template.
     *
     * Each capture is stored in ViewportSnapshotService under the config's label.
     * Returns an ordered map of configId → stored snapshot ID so the caller can
     * identify which snapshots in the service are "template" captures.
     *
     * Configs whose container is missing or whose capture fails are silently skipped.
     *
     * Measurement selection state is saved before capture and restored afterwards
     * so that the user's current selection is not disrupted.
     */
    async captureAll(
        template: IReportSnapshotTemplate,
        containerRefs: Map<string, HTMLDivElement>
    ): Promise<Map<string, string>> {
        const configIdToSnapshotId = new Map<string, string>();

        // Save current measurement selection so we can restore it after capture
        const previousMeasurementId = this.sizingStateService?.getSelectedMeasurementId() ?? null;

        try {
            for (const config of template.configs) {
                const container = containerRefs.get(config.id);
                if (!container) {
                    console.warn(
                        `[ReportSnapshotHandlerService] No container for config "${config.id}" — skipping.`
                    );
                    continue;
                }

                const snapshotId = await this.captureOne(config, container, template.id);
                if (snapshotId) {
                    configIdToSnapshotId.set(config.id, snapshotId);
                }
            }
        } finally {
            // Restore previous measurement selection state
            if (previousMeasurementId) {
                this.sizingStateService?.selectMeasurement(previousMeasurementId);
                this.sizingStateService?.navigateToMeasurement(previousMeasurementId);
            } else {
                this.sizingStateService?.deselectMeasurement();
            }
        }

        return configIdToSnapshotId;
    }

    // =========================================================================
    // Private
    // =========================================================================

    /**
     * Creates a temporary viewport, navigates to the target measurement, captures
     * via ViewportSnapshotService, then disposes the viewport.
     * Returns the stored snapshot ID, or null on failure.
     */
    private async captureOne(
        config: ISnapshotViewportConfig,
        container: HTMLDivElement,
        templateConfigId: string
    ): Promise<string | null> {
        const viewportId = `report-snap-${config.id}`;
        const viewportType = VIEW_MODE_TO_VIEWPORT_TYPE[config.viewMode];

        let handle: {
            viewport: import('@/visualization/viewports/types').IViewport;
            dispose: () => void;
        } | null = null;

        try {
            // 1. Create standalone viewport — the constructor automatically subscribes
            //    to DisplayFeatureManager and CurrentLocationService, so overlays and
            //    volume data are wired up without explicit setup.
            handle = this.viewportService.initStandaloneViewport(viewportId, {
                viewportType,
                container,
                trackResize: false,
                showOrientationWidget: false,
            });

            // 2. Force display objects to rebuild pipelines that depend on subclass
            //    state (e.g. SliceViewport2D.slicePlane) which is not yet available
            //    when BaseViewport's constructor runs initializeExistingFeatures().
            this.viewportService.refreshDisplayObjects(viewportId);

            const { viewport } = handle;

            // 3. Select and navigate to measurement (if specified and sizing state is available).
            //    This activates the overlay (contour + diameter line) on oblique slice
            //    viewports and navigates the viewport to the measurement's node.
            if (config.measurementId && this.sizingStateService) {
                this.sizingStateService.selectMeasurement(config.measurementId);
                this.sizingStateService.navigateToMeasurement(config.measurementId);
            }

            // 4. Apply camera preset (after measurement navigation for 3D views)
            if (config.cameraPreset) {
                viewport.setCameraState(config.cameraPreset);
            }

            // 4b. Apply field of view (explicit or derived from the diameter value).
            //     Applied last: navigateToMeasurement preserves zoom across slice
            //     changes and setCameraState drops parallelScale, so nothing
            //     overrides this.
            if (viewportType === ViewportType.SLICE_OBLIQUE) {
                const fovMm = resolveSnapshotFieldOfViewMm(config, (id) =>
                    this.sizingStateService?.getMeasurement(id)
                );
                if (fovMm !== null) {
                    this.viewportService.setObliqueFieldOfViewMm(viewportId, fovMm);
                }
            }

            // 5. Render, then yield one animation frame so the freshly created
            //    viewport's GPU textures finish uploading before we read them back —
            //    otherwise the very first capture can come out blank/black.
            viewport.forceRender();
            await nextAnimationFrame();

            // 6. Delegate capture + storage to ViewportSnapshotService
            const snapshot = await this.snapshotService.captureViewportById(
                viewportId,
                config.label,
                templateConfigId,
                config.id
            );
            if (!snapshot) {
                console.warn(
                    `[ReportSnapshotHandlerService] captureViewportById returned null for "${config.id}".`
                );
                return null;
            }

            return snapshot.id;
        } catch (err) {
            console.error(`[ReportSnapshotHandlerService] Failed to capture "${config.id}":`, err);
            return null;
        } finally {
            // Always dispose the temporary viewport to free GPU resources
            handle?.dispose();
        }
    }
}
